import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CustomHttpErrorResponse } from '../../../../common';
import { ListServerPaginationStateModel, LIST_SERVER_PAGINATION_INITIAL_STATE } from '../../../../common/models';
import { NotificationService } from '../../../../common/services/notification/notification.service';
import { AddressListItemBrief } from '../models';
import { AddressListExportToFileStart, AddressListInitialState, AddressListLoad } from './address-list.actions';
import { AddressListService } from './address-list.service';
import { NavigateUserService } from '../../../services/navigate-user.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export type AddressListStateModel = ListServerPaginationStateModel<AddressListItemBrief>;

const initialState: AddressListStateModel = LIST_SERVER_PAGINATION_INITIAL_STATE;

@State<AddressListStateModel>({
  name: 'addressList',
  defaults: initialState,
})
@Injectable()
export class AddressListState {
  constructor(
    public addressListService: AddressListService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getState(state: AddressListStateModel): AddressListStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: AddressListStateModel): boolean {
    return state.loaded;
  }

  @Action(AddressListLoad)
  getAddressList(
    ctx: StateContext<AddressListStateModel>,
    { payload }: AddressListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.addressListService.getList(payload).pipe(
      take(1),
      map((value: AddressListStateModel) => {
        ctx.patchState({ ...value, loaded: true });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AddressListInitialState)
  setInitialState(ctx: StateContext<AddressListStateModel>): void {
    ctx.setState(initialState);
  }

  /**
   *  starting export excel file
   */
  @Action(AddressListExportToFileStart)
  startExportToFile(
    ctx: StateContext<AddressListStateModel>,
    { payload: { dialogRef, addressExport, ...body } }: AddressListExportToFileStart,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.addressListService.startExportToFile(body, addressExport).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.export.success.exportStart');
        dialogRef.close();
        if (body.navigationNeeded) {
          this.navigateUserService.navigateUserToAbsolutePath('/dashboard/exports');
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }
}
